//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '@/_utils/axios';
import { LoadingStatuses } from '@/constants/constants';
import { FocusVersionScheduling, FocusSchedulingStats, PolarisApiResponse } from '@/redux/slices/slice_types';

export type FocusCalendarViews = 'day' | 'week' | 'month' | '3 months';

type FocusCalendar = {
  view: FocusCalendarViews;
  availsById: Record<
    string,
    | { loadingStatus: LoadingStatuses.pending; data: null }
    | { loadingStatus: LoadingStatuses.rejected; data: { success: false; message: string } | undefined; loadedAt: string }
    | { loadingStatus: LoadingStatuses.fulfilled; data: FocusVersionScheduling; loadedAt: string }
  >;
  stats: FocusSchedulingStats[] | null;
  statsStatus: LoadingStatuses;
  selectedAvailId: number | null;
  isAvailExpanded: boolean; // indicates that selected avail should be displayed expanded
  sidePanelTransitionState: 'exiting' | 'exited' | null;
};

const initialState: FocusCalendar = {
  view: 'week',
  availsById: {},
  stats: null,
  statsStatus: LoadingStatuses.fulfilled,
  selectedAvailId: null,
  isAvailExpanded: false,
  sidePanelTransitionState: 'exited'
};

export const loadAvailDetails = createAsyncThunk<
  FocusVersionScheduling,
  number,
  {
    rejectValue: { success: false; message: string };
  }
>('focusCalendar/loadAvailDetails', async (availId: number, { rejectWithValue }) => {
  return axios
    .get(`/focus/scheduling/summary/${availId}/`)
    .then((res) => res.data.data)
    .catch((e) => rejectWithValue(e.data));
});

export const getCalendarStats = createAsyncThunk(
  'focusCalendar/getCalendarStats',
  async ({ appliedFilters }: { appliedFilters: Record<string, any> }, { signal }) => {
    const { term_start, ...restFilters } = appliedFilters;
    const filterSet = restFilters.search === '' ? { ...restFilters, search: undefined } : restFilters;

    return axios
      .post<PolarisApiResponse<FocusSchedulingStats[]>>(
        '/focus/scheduling/v2/calendar-stats/',
        {
          ...term_start,
          ...filterSet,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        { signal, baseURL: '/api/v1.1' }
      )
      .then((res) => res.data);
  }
);

export const CALENDAR_PANELS_TRANSITION_DURATION = 300;

export const hideSidePanel = createAsyncThunk('focusCalendar/hideSidePanel', () => {
  return new Promise((resolve) => setTimeout(resolve, CALENDAR_PANELS_TRANSITION_DURATION));
});

const FocusCalendarSlice = createSlice({
  name: 'focusCalendar',
  initialState,
  reducers: {
    clearCache: (state) => {
      state.availsById = initialState.availsById;
      state.selectedAvailId = initialState.selectedAvailId;
      state.isAvailExpanded = initialState.isAvailExpanded;
      state.sidePanelTransitionState = initialState.sidePanelTransitionState;
      state.stats = null;
      state.statsStatus = LoadingStatuses.fulfilled;
    },
    setView: (state, { payload }: { payload: FocusCalendarViews }) => {
      state.view = payload;
    },
    setSelectedAvailId: (state, { payload }: { payload: FocusCalendar['selectedAvailId'] }) => {
      state.selectedAvailId = payload;
      if (payload !== null) {
        state.sidePanelTransitionState = null;
      }
    },
    setIsAvailExpanded: (state, { payload }: { payload: boolean }) => {
      state.isAvailExpanded = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadAvailDetails.pending, (state, action) => {
      state.availsById[action.meta.arg] = {
        loadingStatus: LoadingStatuses.pending,
        data: null
      };
    });
    builder.addCase(loadAvailDetails.rejected, (state, action) => {
      state.availsById[action.meta.arg] = {
        loadingStatus: LoadingStatuses.rejected,
        data: action.payload,
        loadedAt: new Date().toISOString()
      };
    });
    builder.addCase(loadAvailDetails.fulfilled, (state, action) => {
      state.availsById[action.meta.arg] = {
        loadingStatus: LoadingStatuses.fulfilled,
        data: action.payload,
        loadedAt: new Date().toISOString()
      };
    });
    builder.addCase(getCalendarStats.pending, (state) => {
      state.statsStatus = LoadingStatuses.pending;
    });
    builder.addCase(getCalendarStats.fulfilled, (state, { payload }) => {
      state.stats = payload.data || [];
      state.statsStatus = LoadingStatuses.fulfilled;
    });
    builder.addCase(getCalendarStats.rejected, (state) => {
      state.statsStatus = LoadingStatuses.rejected;
    });
    builder.addCase(hideSidePanel.pending, (state) => {
      state.isAvailExpanded = false;
      state.sidePanelTransitionState = 'exiting';
    });
    builder.addCase(hideSidePanel.fulfilled, (state) => {
      state.sidePanelTransitionState = 'exited';
      state.selectedAvailId = null;
    });
  }
});

export default FocusCalendarSlice.reducer;

export const { clearCache, setView, setSelectedAvailId, setIsAvailExpanded } = FocusCalendarSlice.actions;
